/**
 * What a player believes about the ball, as opposed to what is true.
 *
 * Every tick the match photographs the ball's whole three-second forecast and keeps the last
 * thirty photographs. A player then reads the photograph from as many milliseconds ago as
 * his reaction time — so a slow defender is genuinely acting on older information than a
 * sharp striker beside him, and both are acting on older information than the ball has.
 * That delay is the entire reaction-time model; there is no separate "reaction" anywhere.
 *
 * The one safety valve: a belief is never allowed to be more than 2.5 m away from the truth
 * (`maxDistanceDeviation`, mentalimage.cpp:11). Without it a player who did not see a pass
 * being struck would keep running at where the ball used to be for a fifth of a second,
 * which reads as stupidity rather than as slow reactions.
 *
 * Source: mentalimage.cpp, match.cpp:637-649 and :887-895.
 *
 * The same photograph also contains every player. Positions are extrapolated by the age of
 * the image when read back, then pulled to within 2.5 m of reality; movement is pulled to
 * within walking speed. This is deliberately a view over tiny structural interfaces rather
 * than over MatchPlayer, so perception does not depend on the match/controller modules.
 */

import { Vector3 } from 'three';
import { BALL_PREDICTION_STEPS } from '../core/constants';
import type { Ball } from './ball';
import type { FormationEntry, PlayerRole } from './formation';

/** mentalimage.cpp:11 — how far a belief may drift from reality before it is dragged back. */
const MAX_DISTANCE_DEVIATION = 2.5;
const MAX_MOVEMENT_DEVIATION = 5.0;

/** How many photographs the match keeps. match.cpp:890. */
export const MENTAL_IMAGE_COUNT = 30;

export interface MentalImagePlayerSource {
  readonly id: number;
  readonly active: boolean;
  readonly teamId: number;
  readonly side: -1 | 1;
  readonly position: Vector3;
  readonly directionVec: Vector3;
  readonly bodyDirectionVec: Vector3;
  readonly floatVelocity: number;
  readonly movement: Vector3;
  readonly formationEntry: FormationEntry;
  readonly dynamicFormationEntry: FormationEntry;
}

export interface MentalImagePlayer {
  readonly id: number;
  readonly teamId: number;
  readonly side: -1 | 1;
  readonly position: Vector3;
  readonly directionVec: Vector3;
  readonly bodyDirectionVec: Vector3;
  readonly floatVelocity: number;
  readonly movement: Vector3;
  readonly formationEntry: FormationEntry;
  readonly dynamicFormationEntry: FormationEntry;
  readonly role: PlayerRole;
  readonly dynamicRole: PlayerRole;
}

interface StoredPlayer {
  source: MentalImagePlayerSource;
  position: Vector3;
  directionVec: Vector3;
  bodyDirectionVec: Vector3;
  floatVelocity: number;
  movement: Vector3;
  formationEntry: FormationEntry;
  dynamicFormationEntry: FormationEntry;
}

/**
 * `Vector3::EnforceMaximumDeviation` (vector3.cpp:256-265) — pull `belief` toward `truth`
 * until they are at most `maxDeviation` apart, and no further.
 */
export function enforceMaximumDeviation(
  belief: Vector3,
  truth: Vector3,
  maxDeviation: number,
  out = new Vector3(),
): Vector3 {
  out.copy(belief);
  const dx = truth.x - belief.x;
  const dy = truth.y - belief.y;
  const dz = truth.z - belief.z;
  const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (distance > maxDeviation) {
    const scale = (distance - maxDeviation) / distance;
    out.set(belief.x + dx * scale, belief.y + dy * scale, belief.z + dz * scale);
  }
  return out;
}

export class MentalImage {
  private readonly ballPredictions: Vector3[] = Array.from(
    { length: BALL_PREDICTION_STEPS },
    () => new Vector3(),
  );

  /** How old this photograph is, in milliseconds. Set when the match hands it out. */
  timeStampNegMs = 0;

  private readonly ball: Ball;
  private readonly playerSources: readonly MentalImagePlayerSource[];
  private readonly players = new Map<number, StoredPlayer>();

  // Assigned by hand rather than as a constructor parameter property: with modern class
  // fields those are written after the field initializers above, and the snapshot below
  // would then read an undefined ball. It bites only in the browser build, not in tests.
  constructor(ball: Ball, playerSources: readonly MentalImagePlayerSource[] = []) {
    this.ball = ball;
    this.playerSources = playerSources;
    this.takeSnapshot();
  }

  /** Copy the ball's whole forecast. `MentalImage::UpdateBallPredictions`. */
  takeSnapshot(): void {
    for (let i = 0; i < BALL_PREDICTION_STEPS; i++) {
      this.ballPredictions[i]!.copy(this.ball.predictions[i]!);
    }
    this.players.clear();
    for (const source of this.playerSources) {
      if (!source.active) continue;
      this.players.set(source.id, {
        source,
        position: source.position.clone(),
        directionVec: source.directionVec.clone(),
        bodyDirectionVec: source.bodyDirectionVec.clone(),
        floatVelocity: source.floatVelocity,
        movement: source.movement.clone(),
        formationEntry: cloneFormationEntry(source.formationEntry),
        dynamicFormationEntry: cloneFormationEntry(source.dynamicFormationEntry),
      });
    }
  }

  /** `MentalImage::GetPlayerImage` — a delayed, bounded belief about one player. */
  player(id: number): MentalImagePlayer | undefined {
    const stored = this.players.get(id);
    if (!stored || !stored.source.active) return undefined;
    const source = stored.source;
    const position = stored.position
      .clone()
      .addScaledVector(stored.movement, this.timeStampNegMs * 0.001);
    enforceMaximumDeviation(position, source.position, MAX_DISTANCE_DEVIATION, position);
    const movement = enforceMaximumDeviation(
      stored.movement,
      source.movement,
      MAX_MOVEMENT_DEVIATION,
    );
    return {
      id: source.id,
      teamId: source.teamId,
      side: source.side,
      position,
      directionVec: stored.directionVec.clone(),
      bodyDirectionVec: stored.bodyDirectionVec.clone(),
      floatVelocity: stored.floatVelocity,
      movement,
      formationEntry: cloneFormationEntry(stored.formationEntry),
      dynamicFormationEntry: cloneFormationEntry(stored.dynamicFormationEntry),
      role: stored.formationEntry.role,
      dynamicRole: stored.dynamicFormationEntry.role,
    };
  }

  /**
   * Where this player believes the ball will be `timeMs` from now.
   *
   * The age of the photograph is ADDED to the requested time before the lookup, so an old
   * photograph is read further along its own forecast — which is what makes it line up with
   * the present rather than lag behind it. What lags is the *content*: the forecast was
   * made before whatever has happened since.
   */
  ballPrediction(timeMs: number, out = new Vector3()): Vector3 {
    let index = timeMs + this.timeStampNegMs;
    if (index >= BALL_PREDICTION_STEPS * 10) index = BALL_PREDICTION_STEPS * 10 - 10;
    index = Math.floor(index / 10);
    if (index < 0) index = 0;
    const truth = this.ball.predict(timeMs, _truth);
    return enforceMaximumDeviation(this.ballPredictions[index]!, truth, MAX_DISTANCE_DEVIATION, out);
  }
}

const _truth = new Vector3();

/**
 * The ring of photographs, newest first.
 *
 * Fixed size and recycled in place: the original allocates and frees one every tick, which
 * is exactly the sort of thing a browser should not be doing a hundred times a second.
 */
export class MentalImageRing {
  private readonly images: MentalImage[];
  /** Index of the newest photograph. */
  private head = 0;

  constructor(
    ball: Ball,
    players: readonly MentalImagePlayerSource[] = [],
    count = MENTAL_IMAGE_COUNT,
  ) {
    this.images = Array.from({ length: count }, () => new MentalImage(ball, players));
  }

  /** Take this tick's photograph, overwriting the oldest. */
  advance(): void {
    this.head = (this.head - 1 + this.images.length) % this.images.length;
    this.images[this.head]!.takeSnapshot();
  }

  /** Re-photograph the newest image, after a touch has changed the future. */
  refreshLatest(): void {
    this.images[this.head]!.takeSnapshot();
  }

  /** The photograph from `historyMs` ago, clamped to what is kept. `Match::GetMentalImage`. */
  get(historyMs: number): MentalImage {
    let index = Math.round(historyMs / 10);
    if (index >= this.images.length) index = this.images.length - 1;
    if (index < 0) index = 0;
    const image = this.images[(this.head + index) % this.images.length]!;
    image.timeStampNegMs = index * 10;
    return image;
  }
}

function cloneFormationEntry(entry: FormationEntry): FormationEntry {
  return {
    role: entry.role,
    databasePosition: entry.databasePosition.clone(),
    position: entry.position.clone(),
  };
}
